import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../context/UserContext";
import AuthRequiredModal from "../components/AlertComponents/AuthRequiredModal";
import TakeActionCampaignContent, {
  getTakeActionScheduleState,
} from "../components/CampaignComponents/TakeActionCampaignContent";
import {
  completePublicActionCampaign,
  fetchPublicActionCampaign,
  getTakeActionUnavailableTitle,
} from "../utils/campaignApi";
import { lookupPublicRepsByAddress } from "../utils/repLookupUtils";
import { followUser, unfollowUser } from "../utils/discoverApi";
import "../styles/component_styles/Campaigns.scss";

const callCompletionKey = (id) => `take_action_call_completed_${id}`;
const emailCompletionKey = (id) => `take_action_email_completed_${id}`;

function applyCompletionStats(data, setCampaign) {
  const stats = {
    call_completions: data.call_completions ?? data.action_completions ?? 0,
    call_completions_verified: data.call_completions_verified ?? 0,
    email_completions: data.email_completions ?? 0,
    email_completions_verified: data.email_completions_verified ?? 0,
    action_completions: data.call_completions ?? data.action_completions ?? 0,
  };
  setCampaign((prev) => (prev ? { ...prev, ...stats } : prev));
  return stats;
}

export default function TakeActionCampaignPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, userId } = useUser();
  const isLoggedIn = !!user;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorReason, setErrorReason] = useState("unavailable");
  const [campaign, setCampaign] = useState(null);
  const [callCompletions, setCallCompletions] = useState(0);
  const [emailCompletions, setEmailCompletions] = useState(0);
  const [organizerIsFollowing, setOrganizerIsFollowing] = useState(false);
  const [organizerFollowLoading, setOrganizerFollowLoading] = useState(false);
  const [showFollowAuth, setShowFollowAuth] = useState(false);
  const [markingCall, setMarkingCall] = useState(false);
  const [markingEmail, setMarkingEmail] = useState(false);
  const [markingError, setMarkingError] = useState("");
  const [alreadyCompletedCall, setAlreadyCompletedCall] = useState(false);
  const [alreadyCompletedEmail, setAlreadyCompletedEmail] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [copied, setCopied] = useState(false);

  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [viewerReps, setViewerReps] = useState(null);

  const syncCompletionCounts = useCallback((data) => {
    const stats = applyCompletionStats(data, setCampaign);
    setCallCompletions(stats.call_completions);
    setEmailCompletions(stats.email_completions);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchPublicActionCampaign(campaignId);
        if (cancelled) return;
        setCampaign(data);
        syncCompletionCounts(data);
        setOrganizerIsFollowing(Boolean(data.organizer_is_following));
        if (typeof window !== "undefined") {
          if (window.localStorage?.getItem(callCompletionKey(campaignId))) {
            setAlreadyCompletedCall(true);
          }
          if (window.localStorage?.getItem(emailCompletionKey(campaignId))) {
            setAlreadyCompletedEmail(true);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "We could not load this take-action page.");
          setErrorReason(err.reason || "unavailable");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [campaignId, syncCompletionCounts]);

  const scheduleState = useMemo(() => getTakeActionScheduleState(campaign), [campaign]);
  const isLive = scheduleState === "live";

  useEffect(() => {
    if (!campaign || !isLive || loading) return undefined;

    let cancelled = false;
    const refreshCompletions = async () => {
      try {
        const data = await fetchPublicActionCampaign(campaignId);
        if (cancelled) return;
        syncCompletionCounts(data);
      } catch {
        // Ignore background refresh errors.
      }
    };

    const intervalId = window.setInterval(refreshCompletions, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [campaign, campaignId, isLive, loading, syncCompletionCounts]);

  useEffect(() => {
    if (!campaign || scheduleState !== "scheduled" || loading) return undefined;

    let cancelled = false;
    const checkIfOpen = async () => {
      try {
        const data = await fetchPublicActionCampaign(campaignId);
        if (cancelled) return;
        setCampaign(data);
        syncCompletionCounts(data);
        setOrganizerIsFollowing(Boolean(data.organizer_is_following));
      } catch {
        // Ignore background refresh errors.
      }
    };

    const intervalId = window.setInterval(checkIfOpen, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [campaign, campaignId, loading, scheduleState, syncCompletionCounts]);

  const displayTargets = useMemo(() => {
    if (!campaign) return [];
    if (campaign.rep_source === "viewer_reps") {
      if (!viewerReps) return [];
      const normalized = viewerReps;
      const targets = [];
      if (normalized.rep) {
        targets.push({
          name: normalized.rep.name,
          title: normalized.rep.title,
          phone: normalized.rep.phone,
          party: normalized.rep.party,
        });
      }
      [normalized.senOne, normalized.senTwo].filter(Boolean).forEach((senator) => {
        targets.push({
          name: senator.name,
          title: senator.title,
          phone: senator.phone,
          party: senator.party,
        });
      });
      return targets;
    }
    return (campaign.custom_targets || []).map((t) => ({
      name: t.name,
      title: t.title,
      phone: t.phone,
      party: t.party,
      url: t.url,
    }));
  }, [campaign, viewerReps]);

  const handleAddressLookup = async (addressPayload) => {
    setLookupLoading(true);
    setLookupError("");
    try {
      const data = await lookupPublicRepsByAddress(addressPayload);
      setViewerReps(data);
    } catch (err) {
      setLookupError(err.message || "Could not find representatives for that address.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleOrganizerFollowToggle = useCallback(async () => {
    const organizerId = campaign?.organizer_id;
    if (!organizerId || organizerFollowLoading) return;
    if (!isLoggedIn) {
      setShowFollowAuth(true);
      return;
    }
    if (organizerId === userId) return;

    const wasFollowing = organizerIsFollowing;
    setOrganizerFollowLoading(true);
    setOrganizerIsFollowing(!wasFollowing);
    try {
      if (wasFollowing) {
        await unfollowUser(organizerId);
      } else {
        await followUser(organizerId);
      }
    } catch {
      setOrganizerIsFollowing(wasFollowing);
    } finally {
      setOrganizerFollowLoading(false);
    }
  }, [campaign?.organizer_id, isLoggedIn, organizerFollowLoading, organizerIsFollowing, userId]);

  const handleMarkCallComplete = useCallback(async () => {
    if (!isLive || alreadyCompletedCall || markingCall) return;
    const previousCount = callCompletions;
    setMarkingCall(true);
    setMarkingError("");
    setCallCompletions((count) => count + 1);
    setAlreadyCompletedCall(true);
    try {
      const data = await completePublicActionCampaign(campaignId, "call");
      const stats = applyCompletionStats(data, setCampaign);
      setCallCompletions(stats.call_completions);
      setEmailCompletions(stats.email_completions);
      try {
        window.localStorage?.setItem(callCompletionKey(campaignId), "1");
      } catch {
        // ignore storage failures
      }
      if (!isLoggedIn) setShowAuth(true);
    } catch (err) {
      setCallCompletions(previousCount);
      setAlreadyCompletedCall(false);
      setMarkingError(err.message || "Could not record your call right now. Please try again.");
    } finally {
      setMarkingCall(false);
    }
  }, [isLive, alreadyCompletedCall, markingCall, campaignId, callCompletions, isLoggedIn]);

  const handleMarkEmailComplete = useCallback(async () => {
    if (!isLive || alreadyCompletedEmail || markingEmail) return;
    const previousCount = emailCompletions;
    setMarkingEmail(true);
    setMarkingError("");
    setEmailCompletions((count) => count + 1);
    setAlreadyCompletedEmail(true);
    try {
      const data = await completePublicActionCampaign(campaignId, "email");
      const stats = applyCompletionStats(data, setCampaign);
      setCallCompletions(stats.call_completions);
      setEmailCompletions(stats.email_completions);
      try {
        window.localStorage?.setItem(emailCompletionKey(campaignId), "1");
      } catch {
        // ignore storage failures
      }
      if (!isLoggedIn) setShowAuth(true);
    } catch (err) {
      setEmailCompletions(previousCount);
      setAlreadyCompletedEmail(false);
      setMarkingError(err.message || "Could not record your email right now. Please try again.");
    } finally {
      setMarkingEmail(false);
    }
  }, [isLive, alreadyCompletedEmail, markingEmail, campaignId, emailCompletions, isLoggedIn]);

  const handleCopyScript = async () => {
    const text = campaign?.action_script || "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="take-action">
        <div className="campaign-loading">
          <CircularProgress size={28} />
          <p style={{ marginTop: 12 }}>Loading take-action page...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    const errorTitle = getTakeActionUnavailableTitle(errorReason);
    return (
      <div className="take-action">
        <div className="take-action__empty">
          <h1>{errorTitle}</h1>
          <p>{error || "This take-action page could not be loaded."}</p>
          <button type="button" className="campaign-btn" onClick={() => navigate("/")}>
            Go to CivixThread
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="take-action">
      <TakeActionCampaignContent
        mode="live"
        campaign={campaign}
        scheduleState={scheduleState}
        displayTargets={displayTargets}
        callCompletions={callCompletions}
        emailCompletions={emailCompletions}
        isLive={isLive}
        isLoggedIn={isLoggedIn}
        currentUserId={userId}
        organizerIsFollowing={organizerIsFollowing}
        organizerFollowLoading={organizerFollowLoading}
        onOrganizerFollowToggle={handleOrganizerFollowToggle}
        onOrganizerFollowAuthRequired={() => setShowFollowAuth(true)}
        alreadyCompletedCall={alreadyCompletedCall}
        alreadyCompletedEmail={alreadyCompletedEmail}
        markingCall={markingCall}
        markingEmail={markingEmail}
        copied={copied}
        viewerReps={viewerReps}
        lookupLoading={lookupLoading}
        lookupError={lookupError}
        onAddressLookup={handleAddressLookup}
        onClearViewerReps={() => setViewerReps(null)}
        onMarkCallComplete={handleMarkCallComplete}
        onMarkEmailComplete={handleMarkEmailComplete}
        onCopyScript={handleCopyScript}
      />

      {markingError ? (
        <p className="take-action__hint take-action__hint--error" role="alert">
          {markingError}
        </p>
      ) : null}

      <AuthRequiredModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onLogin={() => navigate("/auth/signup")}
        title="Track your civic impact"
        description="Sign up to track calls you make, get bill updates, and stay connected to civic action."
      />
      <AuthRequiredModal
        open={showFollowAuth}
        onClose={() => setShowFollowAuth(false)}
        onLogin={() => navigate("/auth/signup")}
        title="Sign in to follow"
        description="Create a free account to follow this organizer and get updates on their campaigns."
      />
    </div>
  );
}
